import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { AUTHENTICATION_SERVICE } from '@my-backoffice/app/shared/auth/authentication.token';
import { EAppcuesEvent } from '@my-backoffice/app/shared/models/enums/appcues-event.enum';
import { EAppcuesType } from '@my-backoffice/app/shared/models/enums/appcues-type.enum';
import { IAppcuesEvent } from '@my-backoffice/app/shared/models/interfaces/appcues-event.interface';
import { ScriptLoaderService } from '@my-backoffice/app/shared/services/third-party-services/script-loader/script-loader.service';
import { APPCUES_ACCOUNT_ID } from '@my-backoffice/app/shared/tokens/appcues-account-id.token';
import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { QimaLoggerService, QimaOptionalType } from '@qima/ngx-qima';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppcuesService extends ScriptLoaderService {
  private readonly _authenticationService = inject(AUTHENTICATION_SERVICE);

  private readonly _userDataSentToAppcues: readonly string[] = [
    'firstName',
    'lastName',
    'login',
    'email',
    'sic',
    'sicId',
    'companyName',
    'companyId',
    'companyType',
    'companyHierarchyType',
    'newMyQimaUser',
  ];

  private readonly _id: QimaOptionalType<string>;
  private readonly _bannerVisibilityBehaviorSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _hasAppcuesScriptLoaded: boolean = false;

  public constructor(
    private readonly _qimaLoggerService: QimaLoggerService,
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(APPCUES_ACCOUNT_ID) private readonly _appcuesId: Readonly<string>
  ) {
    super();
    this._id = this._appcuesId;

    if (this._id && !isNaN(parseInt(this._id, 10))) {
      this._data = {
        src: `//fast.appcues.com/${this._id}.js`,
      };
    }
  }

  public appcuesEvent$(): Observable<boolean> {
    return this._bannerVisibilityBehaviorSubject$.asObservable();
  }

  public toggleBanner(isVisible: boolean): void {
    this._bannerVisibilityBehaviorSubject$.next(isVisible);
  }

  public appcuesLoader$(): Observable<void> {
    if (!this._data.src || this._hasAppcuesScriptLoaded) {
      return of(undefined);
    }

    return from(this._loadScript()).pipe(
      switchMap((): Observable<void> => {
        this._registerUserAndEvents(this._authenticationService.getCurrentUser());
        this._hasAppcuesScriptLoaded = true;

        return of(void 0);
      }),
      catchError((err): Observable<void> => {
        console.error('unable to load Appcues', err);
        this._qimaLoggerService.debug('unable to load Appcues', err);

        return of(void 0);
      })
    );
  }

  private _registerUserAndEvents(userData: Partial<IUser>): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const windowRef = this._document.defaultView as any;

    if (!windowRef.Appcues) {
      return;
    }

    const userProperties = {
      // Give only the values you need to a third party!
      ...this._filterUserData(userData),
    };

    // Only identify if user has an id
    if (userData.id) {
      windowRef.Appcues.identify(userData.id, userProperties);
    } else {
      windowRef.Appcues.identify('payment_portal.anonymous', {
        ...userProperties,
        isPaymentPortalUser: true,
      });
    }

    windowRef.Appcues.on('all', (_name: string, event: IAppcuesEvent): void => this._onBannerBroadcast(event));
  }

  private _filterUserData(initialData: Partial<IUser>): Partial<IUser> {
    const allowedKeys = this._userDataSentToAppcues;
    const filteredData: Record<string, unknown> = {};

    for (const key of allowedKeys) {
      const value = initialData[key as keyof IUser];

      if (value !== undefined) {
        filteredData[key] = value;
      }
    }

    return filteredData as Partial<IUser>;
  }

  private _onBannerBroadcast(event: IAppcuesEvent): void {
    const isBannerEvent = event?.experienceType === EAppcuesType.BANNER || event?.stepType === EAppcuesType.BANNER;

    if (isBannerEvent) {
      const isAppcuesBannerVisible = event.name === EAppcuesEvent.STEP_SEEN || event.name === EAppcuesEvent.EXPERIENCE_STARTED;

      this.toggleBanner(isAppcuesBannerVisible);
    }
  }
}
